import { parseDate, partsInZone, shiftDate, zonedLocalToUtc } from "./time";

export type BusyInterval = { start: number; end: number };

const GIS_SRC = "https://accounts.google.com/gsi/client";
const TOKEN_KEY = "overlap_gcal_token";
const TOKEN_EXP_KEY = "overlap_gcal_exp";
const CLIENT_KEY = "overlap_google_client_id";
const ICS_KEY = "overlap_ics";

type TokenClient = {
  requestAccessToken: (override?: { prompt?: string }) => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (resp: { access_token?: string; error?: string; expires_in?: number }) => void;
            error_callback?: (err: { type?: string; message?: string }) => void;
          }) => TokenClient;
        };
      };
    };
  }
}

export function googleClientId(): string {
  const fromEnv = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ?? "";
  if (fromEnv) return fromEnv;
  return (localStorage.getItem(CLIENT_KEY) ?? "").trim();
}

export function setGoogleClientId(id: string) {
  const trimmed = id.trim();
  if (trimmed) localStorage.setItem(CLIENT_KEY, trimmed);
  else localStorage.removeItem(CLIENT_KEY);
}

export function slotConflicts(start: Date, durationMin: number, busy: BusyInterval[]): boolean {
  const a0 = start.getTime();
  const a1 = a0 + durationMin * 60_000;
  return busy.some((b) => a0 < b.end && b.start < a1);
}

export function encodeBusy(busy: BusyInterval[]): string {
  return busy
    .map((b) => `${Math.floor(b.start / 1000)}-${Math.floor(b.end / 1000)}`)
    .join("_");
}

export function decodeBusy(raw: string | null): BusyInterval[] {
  if (!raw) return [];
  const out: BusyInterval[] = [];
  for (const part of raw.split("_")) {
    const [a, b] = part.split("-").map(Number);
    if (Number.isFinite(a) && Number.isFinite(b) && b > a) {
      out.push({ start: a * 1000, end: b * 1000 });
    }
  }
  return out;
}

export function savedIcs(): string {
  return sessionStorage.getItem(ICS_KEY) ?? "";
}

export function saveIcs(text: string) {
  sessionStorage.setItem(ICS_KEY, text);
}

export function clearCalendarSession() {
  sessionStorage.removeItem(ICS_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXP_KEY);
}

export function savedGoogleToken(): string | null {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(TOKEN_EXP_KEY) ?? 0);
  if (!token || Date.now() > exp) return null;
  return token;
}

function storeGoogleToken(token: string, expiresIn: number) {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + Math.max(60, expiresIn - 60) * 1000));
}

function loadGis(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google sign-in failed to load")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google sign-in failed to load"));
    document.head.appendChild(script);
  });
}

export async function connectGoogle(): Promise<string> {
  const clientId = googleClientId();
  if (!clientId) throw new Error("missing-client-id");
  await loadGis();
  const oauth = window.google?.accounts.oauth2;
  if (!oauth) throw new Error("Google sign-in unavailable");
  return new Promise((resolve, reject) => {
    const client = oauth.initTokenClient({
      client_id: clientId,
      scope: "https://www.googleapis.com/auth/calendar.freebusy",
      callback: (resp) => {
        if (resp.error || !resp.access_token) {
          reject(new Error(resp.error || "Google sign-in was cancelled"));
          return;
        }
        storeGoogleToken(resp.access_token, resp.expires_in ?? 3600);
        resolve(resp.access_token);
      },
      error_callback: (err) => {
        reject(new Error(err.message || err.type || "Google sign-in was cancelled"));
      },
    });
    client.requestAccessToken({ prompt: savedGoogleToken() ? "" : "consent" });
  });
}

export async function fetchGoogleBusy(token: string, timeMin: Date, timeMax: Date): Promise<BusyInterval[]> {
  const res = await fetch("https://www.googleapis.com/calendar/v3/freeBusy", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString(),
      items: [{ id: "primary" }],
    }),
  });
  if (res.status === 401) {
    sessionStorage.removeItem(TOKEN_KEY);
    throw new Error("Google session expired — connect again");
  }
  if (!res.ok) throw new Error("Could not read Google Calendar");
  const data = (await res.json()) as {
    calendars?: { primary?: { busy?: { start: string; end: string }[] } };
  };
  const busy = data.calendars?.primary?.busy ?? [];
  return busy.map((row) => ({ start: Date.parse(row.start), end: Date.parse(row.end) })).filter((row) => row.end > row.start);
}

export function dayRange(isoDate: string, timeZone: string): { start: Date; end: Date } {
  const { year, month, day } = parseDate(isoDate);
  const start = zonedLocalToUtc(year, month, day, 0, 0, timeZone);
  const next = parseDate(shiftDate(isoDate, 1));
  const end = zonedLocalToUtc(next.year, next.month, next.day, 0, 0, timeZone);
  return { start, end };
}

function unfoldIcs(text: string): string {
  return text.replace(/\r\n/g, "\n").replace(/\n[ \t]/g, "");
}

function icsTime(value: string, tzid: string | undefined, fallbackTz: string): number {
  const compact = value.replace(/[-:]/g, "");
  if (/^\d{8}$/.test(compact)) {
    const y = Number(compact.slice(0, 4));
    const m = Number(compact.slice(4, 6));
    const d = Number(compact.slice(6, 8));
    return zonedLocalToUtc(y, m, d, 0, 0, tzid || fallbackTz).getTime();
  }
  const match = compact.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z)?$/);
  if (!match) return Number.NaN;
  const y = Number(match[1]);
  const mo = Number(match[2]);
  const d = Number(match[3]);
  const h = Number(match[4]);
  const mi = Number(match[5]);
  const z = Boolean(match[7]);
  if (z) return Date.UTC(y, mo - 1, d, h, mi, 0);
  const zone = tzid && tzid.includes("/") ? tzid : fallbackTz;
  return zonedLocalToUtc(y, mo, d, h, mi, zone).getTime();
}

function weekdayCode(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" })
    .format(date)
    .slice(0, 2)
    .toUpperCase();
}

function rruleHitsDay(
  rrule: string,
  eventStart: number,
  dayStart: number,
  dayEnd: number,
  timeZone: string,
): boolean {
  const parts = Object.fromEntries(
    rrule
      .split(";")
      .map((bit) => bit.split("="))
      .filter((pair) => pair.length === 2) as [string, string][],
  );
  const freq = parts.FREQ;
  if (freq !== "DAILY" && freq !== "WEEKLY") return false;
  if (parts.UNTIL) {
    const until = icsTime(parts.UNTIL, undefined, "UTC");
    if (Number.isFinite(until) && dayStart > until) return false;
  }
  if (dayEnd <= eventStart) return false;
  const interval = Number(parts.INTERVAL ?? 1);
  const dayMid = new Date((dayStart + dayEnd) / 2);
  if (freq === "WEEKLY") {
    const days = (parts.BYDAY ?? weekdayCode(new Date(eventStart), timeZone)).split(",");
    if (!days.includes(weekdayCode(dayMid, timeZone))) return false;
    const weeks = Math.round((dayStart - eventStart) / (7 * 24 * 3600_000));
    if (weeks < 0 || weeks % interval !== 0) return false;
  } else {
    const days = Math.round((dayStart - eventStart) / (24 * 3600_000));
    if (days < 0 || days % interval !== 0) return false;
  }
  return true;
}

export function parseIcs(text: string, fallbackTz: string, rangeStart: Date, rangeEnd: Date): BusyInterval[] {
  const blocks = unfoldIcs(text).split("BEGIN:VEVENT").slice(1);
  const out: BusyInterval[] = [];
  const r0 = rangeStart.getTime();
  const r1 = rangeEnd.getTime();
  for (const block of blocks) {
    const body = block.split("END:VEVENT")[0] ?? "";
    if (/^TRANSP:TRANSPARENT$/m.test(body) || /^STATUS:CANCELLED$/m.test(body)) continue;
    const startLine = body.match(/^DTSTART([^:]*):(\S+)/m);
    const endLine = body.match(/^DTEND([^:]*):(\S+)/m);
    if (!startLine) continue;
    const startTzid = startLine[1].match(/TZID=([^;]+)/)?.[1];
    const start = icsTime(startLine[2], startTzid, fallbackTz);
    if (!Number.isFinite(start)) continue;
    let end = endLine ? icsTime(endLine[2], endLine[1].match(/TZID=([^;]+)/)?.[1], fallbackTz) : start + 60 * 60_000;
    if (!Number.isFinite(end) || end <= start) end = start + 60 * 60_000;
    const rrule = body.match(/^RRULE:(\S+)/m)?.[1];
    if (rrule) {
      if (!rruleHitsDay(rrule, start, r0, r1, fallbackTz)) continue;
      const zone = startTzid && startTzid.includes("/") ? startTzid : fallbackTz;
      const clock = partsInZone(new Date(start), zone);
      const day = partsInZone(rangeStart, fallbackTz);
      const instStart = zonedLocalToUtc(day.year, day.month, day.day, clock.hour, clock.minute, zone).getTime();
      const inst = { start: instStart, end: instStart + (end - start) };
      if (inst.start < r1 && inst.end > r0) out.push(inst);
      continue;
    }
    if (start < r1 && end > r0) out.push({ start, end });
  }
  return out;
}
